import { Button } from '@/components/button';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import { Modal } from '@/components/modal';
import { clearErrors } from '@/reducers/auth';
import { AppDispatch } from '@/root/store';
import { FormatCurrency } from '@/routes/root';
import {
  GetBankAccounts,
  ShowPendingBalance,
  ShowSettledBalance,
  StoreWithdraw,
} from '@/services/financial';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import {
  ArrowDownOnSquareIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { DashboardCard } from '../../../dashboard/_card';
import { SectionItem, SectionTabs } from '../../../_nav/section';

export function OrgCommerceLayout() {
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const { slug } = useParams();

  const pathArray = location.pathname.split('/').slice(2);
  const section = pathArray[2];

  const [withdrawModal, setWithdrawModal] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    bank_account_id: '',
    account_name: '',
    account_number: '',
  });

  const { pendingWallet, settledWallet, withdraw, bankAccounts, errors } =
    useSelector((state: any) => state.financial);

  const setData = (value: string, element: string) => {
    setFormData({ ...formData, [element]: value });

    dispatch(clearErrors());
  };

  const submit = () => {
    dispatch(StoreWithdraw(slug, formData));
  };

  useEffect(() => {
    dispatch(ShowPendingBalance(slug));
    dispatch(ShowSettledBalance(slug));
    dispatch(GetBankAccounts(slug));
  }, [slug]);

  useEffect(() => {
    if (withdraw) {
      dispatch(ShowSettledBalance(slug));

      setWithdrawModal(false);
    }
  }, [withdraw]);

  useEffect(() => {
    setFormData({ ...formData, bank_account_id: bankAccounts?.[0]?.id });
  }, [bankAccounts]);

  return !section ? (
    <Navigate to='payment' />
  ) : (
    <>
      <Modal
        show={withdrawModal}
        onRequestClose={() => setWithdrawModal(false)}
        width='medium'
        title='Tarik saldo'
        body={
          <div className='flex flex-col gap-4'>
            <FormLabel
              label='Jumlah saldo yang ingin ditarik'
              helperText='Minimal penarikan Rp50.000'
              errorText={errors.amount}
              required
            >
              <Input
                leftDeco='Rp'
                type='price'
                onChange={(e) => setData(e.target.value, 'amount')}
                required
              />
            </FormLabel>

            <FormLabel
              label='No. rekening tujuan'
              errorText={errors.account_number}
              required
            >
              <Input
                type='text'
                disabled
                value={bankAccounts?.[0]?.account_number}
                onChange={(e) => setData(e.target.value, 'account_number')}
                required
              />
            </FormLabel>

            <FormLabel
              label='Nama pemilik rekening'
              errorText={errors.account_name}
              required
            >
              <Input
                type='text'
                disabled
                value={bankAccounts?.[0]?.account_name}
                onChange={(e) => setData(e.target.value, 'account_name')}
                required
              />
            </FormLabel>
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setWithdrawModal(false)}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button onClick={submit} leftIcon={<DocumentCheckIcon />}>
              Simpan
            </Button>
          </div>
        }
      />
      <div className='flex gap-6'>
        <DashboardCard
          variant='white'
          name='Saldo penjualan yang belum dapat ditarik'
          topLabel={{
            primary: <FormatCurrency amount={pendingWallet.balance} />,
          }}
          className='w-[268px]'
        />
        <DashboardCard
          variant='whiteAlt'
          name='Saldo penjualan yang dapat ditarik'
          topLabel={{
            primary: <FormatCurrency amount={settledWallet.balance} />,
          }}
          bottomLabel={{
            primary: (
              <Button
                size='sm'
                leftIcon={<ArrowDownOnSquareIcon />}
                onClick={() => setWithdrawModal(true)}
              >
                Tarik saldo
              </Button>
            ),
          }}
          className='w-[268px]'
        />
      </div>
      <SectionTabs>
        <SectionItem
          id='payment'
          name='Riwayat pemasukan'
          isCurrent={section === 'payment'}
        />
        <SectionItem
          id='withdrawal'
          name='Riwayat penarikan'
          isCurrent={section === 'withdrawal'}
        />
        <SectionItem
          id='banks'
          name='Rekening bank'
          isCurrent={section === 'banks'}
        />
      </SectionTabs>
      <Outlet />
    </>
  );
}
